import { ChronoUnit, type LocalDate, ZonedDateTime } from "@js-joda/core";
import { type FC, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { TimePicker, type TimePickerProps } from "react-time-picker";
import { SCHEDULE_MIN_LEAD_MINUTES } from "shared";
import { DateSelector } from "../calendar/DateSelector";
import { Modal, useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";
import {
	fromTimePickerValue,
	toTimePickerValue,
} from "../helpers/timeConverter";

export type SchedulePostSummary = {
	title: string;
	channelName?: string;
	channelImageUrl?: string;
	privacyLabel?: string;
	hasVideo: boolean;
	estimatedYoutubeUrl?: string;
};

interface ModalSchedulePostProps {
	// undefined datetime means post now
	onConfirm: (datetime: ZonedDateTime | undefined) => unknown;
	initial: ZonedDateTime | undefined;
	summary?: SchedulePostSummary;
	/** YouTube only — enforce SCHEDULE_MIN_LEAD_MINUTES before publish time. */
	requireMinLeadMinutes?: boolean;
}

export const ModalSchedulePost: FC<ModalSchedulePostProps> = (props) => {
	const [selected, setSelected] = useState<ZonedDateTime | undefined>(
		props.initial,
	);
	const [scheduleError, setScheduleError] = useState<string | null>(null);
	const { close } = useModal();
	const requireMinLead = props.requireMinLeadMinutes === true;

	const minimumScheduleTime = useMemo(
		() =>
			requireMinLead
				? ZonedDateTime.now().plus(
						SCHEDULE_MIN_LEAD_MINUTES,
						ChronoUnit.MINUTES,
					)
				: ZonedDateTime.now(),
		[requireMinLead],
	);

	const handleDateChange = (date: LocalDate) => {
		if (!selected) {
			return;
		}

		const oldLocalTime = selected.toLocalTime();
		const zone = selected.zone();

		setSelected(ZonedDateTime.of(date, oldLocalTime, zone));
	};

	const handleTimeChange = (value: TimePickerProps["value"]) => {
		if (!selected || !value) {
			return;
		}

		const time = fromTimePickerValue(value);
		const oldLocalDate = selected.toLocalDate();
		const zone = selected.zone();

		setSelected(ZonedDateTime.of(oldLocalDate, time, zone));
	};

	const handleConfirm = () => {
		if (requireMinLead && selected?.isBefore(minimumScheduleTime)) {
			setScheduleError(
				`Scheduled time must be at least ${SCHEDULE_MIN_LEAD_MINUTES} minutes from now.`,
			);
			return;
		}
		if (selected?.isBefore(ZonedDateTime.now())) {
			setScheduleError("Cannot schedule a post in the past.");
			return;
		}
		setScheduleError(null);
		props.onConfirm(selected);
		close();
	};

	const scheduledLabel = selected
		? selected.toLocalDateTime().toString().replace("T", " at ")
		: "Immediately";

	return (
		<Modal size="lg" aria-labelledby="schedule-modal-title">
			<h2 id="schedule-modal-title" className="h5 mb-2">
				Schedule publication
			</h2>
			<p id="schedule-modal-description" className="text-muted mb-3">
				Select when you want to publish your post. Publishing is not instant —
				it may take a couple of minutes after the scheduled time.
			</p>
			{props.summary && (
				<section
					className="border rounded p-3 mb-3 bg-light"
					aria-labelledby="schedule-summary-heading"
				>
					<h3 id="schedule-summary-heading" className="fw-semibold mb-2 h6">
						Confirm schedule
					</h3>
					<div>
						<strong>Title:</strong> {props.summary.title}
					</div>
					{props.summary.channelName && (
						<div className="d-flex align-items-center gap-2 mt-1">
							<strong>Channel:</strong>
							{props.summary.channelImageUrl && (
								<img
									src={props.summary.channelImageUrl}
									alt=""
									width={20}
									height={20}
									className="rounded-circle"
									aria-hidden="true"
								/>
							)}
							{props.summary.channelName}
						</div>
					)}
					{props.summary.privacyLabel && (
						<div>
							<strong>Privacy:</strong> {props.summary.privacyLabel}
						</div>
					)}
					<div>
						<strong>Video:</strong>{" "}
						{props.summary.hasVideo ? "Attached" : "Missing"}
					</div>
					<div>
						<strong>Publish:</strong> {scheduledLabel}
					</div>
					{props.summary.estimatedYoutubeUrl && (
						<div className="mt-1">
							<strong>Estimated URL:</strong>{" "}
							<span className="text-muted">
								{props.summary.estimatedYoutubeUrl}
							</span>
						</div>
					)}
				</section>
			)}
			<fieldset className="my-3" aria-describedby="schedule-modal-description">
				<legend className="visually-hidden">Publish timing</legend>
				<Form.Check
					type="radio"
					name="publish_time"
					id="publish-now"
					label="Publish now"
					onChange={() => setSelected(undefined)}
					checked={!selected}
				/>
				<Form.Check
					type="radio"
					name="publish_time"
					id="publish-later"
					label="Schedule for later"
					onChange={() => setSelected(props.initial ?? ZonedDateTime.now())}
					checked={!!selected}
				/>
			</fieldset>
			{selected && (
				<fieldset className="modal-schedule-post-selector my-3 border-0 p-0">
					<legend className="visually-hidden">Publication date and time</legend>
					<DateSelector
						value={selected.toLocalDate()}
						initial={selected.toLocalDate()}
						onChange={handleDateChange}
					/>
					<div className="px-3">
						<span id="schedule-datetime-label" className="d-block mb-1">
							Publication time
						</span>
						<TimePicker
							onChange={handleTimeChange}
							value={toTimePickerValue(selected.toLocalTime())}
							disableClock
							aria-label="Publication time"
							clearIcon={null}
						/>
					</div>
				</fieldset>
			)}
			{scheduleError && (
				<Form.Text
					id="schedule-modal-error"
					className="text-danger d-block mb-2"
					role="alert"
				>
					{scheduleError}
				</Form.Text>
			)}
			<Button
				onClick={handleConfirm}
				full
				icon={selected ? "schedule" : "send"}
				aria-describedby={scheduleError ? "schedule-modal-error" : undefined}
			>
				{selected ? "Confirm schedule" : "Confirm publish"}
			</Button>
		</Modal>
	);
};
